// ============================================================================
// Property Switcher: header dropdown for owned + co-hosting properties.
// Shows current property, list with checkmark, "+ Add property" (owners only).
// Syncs active id from URL and localStorage; persists on select.
// ============================================================================

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Building2, Plus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useActiveProperty } from "@/contexts/ActivePropertyContext";
import { useAccessibleProperties, type AccessibleProperty } from "@/hooks/useAccessibleProperties";
import { cn } from "@/lib/utils";

function PropertyThumb({
  property,
  className,
}: {
  property: AccessibleProperty | null | undefined;
  className?: string;
}) {
  const url = property?.property_photo_url?.trim() || "";
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded-[6px] bg-muted",
        className,
      )}
    >
      {url ? (
        <img
          src={url}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      ) : (
        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
      )}
    </div>
  );
}

export function PropertySwitcher({ variant = "default" }: { variant?: "default" | "shell" } = {}) {
  const navigate = useNavigate();
  const { activePropertyId, setActivePropertyId } = useActiveProperty() ?? {};
  const { properties, isLoading, canCreateProperties, showSwitcher } = useAccessibleProperties();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const effectiveActiveId = (() => {
    if (activePropertyId && properties.some((p) => p.id === activePropertyId)) return activePropertyId;
    if (properties.length > 0) return properties[0].id;
    return null;
  })();

  useEffect(() => {
    if (isLoading || properties.length === 0) return;
    if (!activePropertyId || !properties.some((p) => p.id === activePropertyId)) {
      const firstId = properties[0].id;
      if (firstId && activePropertyId !== firstId) setActivePropertyId?.(firstId);
    }
  }, [isLoading, properties, activePropertyId, setActivePropertyId]);

  const activeProperty = properties.find((p) => p.id === effectiveActiveId);
  const displayName = activeProperty?.name ?? "Select property";

  const filteredList = search.trim()
    ? properties.filter((p) => p.name.toLowerCase().includes(search.trim().toLowerCase()))
    : properties;

  const handleSelect = (id: string) => {
    setActivePropertyId?.(id);
    setSearch("");
    setOpen(false);
    navigate(`/properties/${id}`);
  };

  const handleAddProperty = () => {
    setSearch("");
    setOpen(false);
    navigate("/properties/new");
  };

  if (!showSwitcher) return null;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        {variant === "shell" ? (
          <button
            type="button"
            className={cn(
              "flex h-12 min-w-[220px] max-w-[300px] items-center gap-2.5 rounded-[15px] bg-white py-0 pl-2.5 pr-3 transition-shadow",
              open ? "shadow-[inset_0_0_0_1.5px_#24a89a]" : "shadow-[inset_0_0_0_1.5px_#e6edf1] hover:shadow-[inset_0_0_0_1.5px_#cfdbe2]",
            )}
          >
            <PropertyThumb property={activeProperty} className="h-7 w-7 rounded-[8px]" />
            <span className="min-w-0 flex-1 truncate text-left text-[15px] font-bold text-[#0f1720]">{displayName}</span>
            <ChevronDown className={cn("h-4 w-4 shrink-0 text-[#64748b] transition-transform", open && "rotate-180")} />
          </button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="min-w-[140px] max-w-[220px] justify-between gap-1.5 font-normal"
          >
            <PropertyThumb property={activeProperty} className="h-5 w-5" />
            <span className="truncate">{displayName}</span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className={cn("p-0", variant === "shell" ? "w-[300px] rounded-[18px] border-0 shadow-[0_24px_60px_rgba(15,23,32,0.18)]" : "w-[280px]")}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          searchRef.current?.focus();
        }}
      >
        <div className="p-2 border-b">
          <Input
            ref={searchRef}
            placeholder="Search properties..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9"
          />
        </div>
        <div className="max-h-[280px] overflow-y-auto p-1">
          {filteredList.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">No properties match</p>
          ) : (
            filteredList.map((p) => (
              <button
                key={p.id}
                type="button"
                className="flex w-full items-center gap-2.5 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(p.id)}
              >
                <PropertyThumb property={p} className="h-7 w-7" />
                <span className="truncate flex-1">{p.name}</span>
                {p.id === effectiveActiveId && (
                  <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                )}
              </button>
            ))
          )}
        </div>
        {canCreateProperties && (
          <div className="border-t p-1">
            <button
              type="button"
              className="flex h-9 w-full items-center gap-2 rounded-sm px-2 text-left text-sm hover:bg-accent"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleAddProperty}
            >
              <Plus className="h-4 w-4" />
              Add property
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
